import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../../app/api/api';
import { Order } from '../../app/data/models/orders/order';
import {
  OrderNotification as ImportedOrderNotification,
  OrderStatusUpdate,
  PaymentStatusUpdate,
} from '../../app/data/models/orders/orderNotification';
import { OrderStatus } from '../../app/data/models/orders/orderStatus';
import {
  createEventSourceCloseThunk,
  createEventSourceThunk,
} from '../../app/managers/eventSourceManager';
import { AppDispatch, store } from '../../app/store/configureStore';

// Extend OrderNotification to support updatedOrders
interface OrderNotification extends ImportedOrderNotification {
  updatedOrders?: Order[];
  createdOrders?: Order[];
  updatedOrderStatuses?: OrderStatusUpdate[];
  updatedPaymentStatuses?: PaymentStatusUpdate[];
}

// Add this enum for connection status
export enum StreamConnectionStatus {
  NotConnected = 'not_connected',
  Connected = 'connected',
  Disconnected = 'disconnected',
}

export interface BillerOrdersState {
  orders: Order[];
  selectedOrder: Order | null;
  isLoading: boolean;
  error: string | null;
  connectionStatus: StreamConnectionStatus;
  selectedOrderNeedsRefresh?: boolean;
}

const initialState: BillerOrdersState = {
  orders: [],
  selectedOrder: null,
  isLoading: false,
  error: null,
  connectionStatus: StreamConnectionStatus.NotConnected,
};

// Fetch orders for the biller view
export const fetchBillerOrdersAsync = createAsyncThunk<
  Order[],
  { restaurantId: string }
>('billerOrders/fetchOrders', async ({ restaurantId }) => {
  // Use the correct API method for fetching orders
  const response = await api.Order.list(restaurantId);
  // The API returns a paged result with { data: Order[], nextPageId: string | null }
  return response.data || [];
});

// Fetch a single order's details
export const fetchBillerOrderDetailsAsync = createAsyncThunk<
  Order,
  { restaurantId: string; orderId: string }
>('billerOrders/fetchOrderDetails', async ({ restaurantId, orderId }) => {
  const response = await api.Order.details(restaurantId, orderId);
  return response;
});

// Set up the event source for order notifications
export const observeOrderChange = () =>
  createEventSourceThunk(
    'biller/orders/observeOrderChange',
    (arg: unknown) => {
      const { restaurantId } = arg as { restaurantId: string };
      return `restaurant/${restaurantId}/order-notification`;
    },
    (dispatch: AppDispatch, event: MessageEvent) => {
      const getState = store.getState;
      (async () => {
        try {
          const data = JSON.parse(event.data);
          dispatch(updateOrders(data));
          // Check if selectedOrderNeedsRefresh is true after updateOrders
          const state = getState().billerOrders;
          if (state.selectedOrderNeedsRefresh && state.selectedOrder) {
            await dispatch(
              fetchBillerOrderDetailsAsync({
                restaurantId: state.selectedOrder.restaurantId.toString(),
                orderId: state.selectedOrder.id.toString(),
              })
            );
          }
        } catch (error) {
          // Log error but don't throw to keep connection alive
          console.error('Error processing order notification:', error);
        }
      })();
    },
    (dispatch, isConnected) => {
      dispatch(
        setConnectionStatus(
          isConnected
            ? StreamConnectionStatus.Connected
            : StreamConnectionStatus.Disconnected
        )
      );

      // If we're disconnected, try to re-establish the connection after a delay
      if (!isConnected) {
        setTimeout(() => {
          dispatch(setConnectionStatus(StreamConnectionStatus.NotConnected));
        }, 5000); // After 5 seconds, set to NotConnected to allow manual reconnection
      }
    }
  );

export const closeOrderObserver = createEventSourceCloseThunk(
  'biller/orders/observeOrderChange'
);

// Helper function to update orders based on notifications
const processOrderNotification = (
  orders: Order[],
  notification: OrderNotification
): Order[] => {
  const updatedOrders = [...orders];

  // If the notification contains full updated orders (with payments/refunds), merge them in
  if (notification.updatedOrders && notification.updatedOrders.length > 0) {
    notification.updatedOrders.forEach((updatedOrder: Order) => {
      const idx = updatedOrders.findIndex((o) => o.id === updatedOrder.id);
      if (idx !== -1) {
        updatedOrders[idx] = { ...updatedOrder };
      } else {
        updatedOrders.unshift(updatedOrder);
      }
    });
  }

  // Handle created orders
  if (notification.createdOrders && notification.createdOrders.length > 0) {
    notification.createdOrders.forEach((newOrder: Order) => {
      if (!updatedOrders.some((o) => o.id === newOrder.id)) {
        updatedOrders.unshift(newOrder);
      }
    });
  }

  // Handle status updates
  if (
    notification.updatedOrderStatuses &&
    notification.updatedOrderStatuses.length > 0
  ) {
    notification.updatedOrderStatuses.forEach(
      (statusUpdate: OrderStatusUpdate) => {
        const orderIndex = updatedOrders.findIndex(
          (o) => o.id === statusUpdate.orderId
        );
        if (orderIndex !== -1) {
          updatedOrders[orderIndex] = {
            ...updatedOrders[orderIndex],
            orderStatus: parseInt(statusUpdate.status, 10) as OrderStatus,
          };
        }
      }
    );
  }

  // Handle payment updates
  if (
    notification.updatedPaymentStatuses &&
    notification.updatedPaymentStatuses.length > 0
  ) {
    notification.updatedPaymentStatuses.forEach(
      (paymentUpdate: PaymentStatusUpdate) => {
        const orderIndex = updatedOrders.findIndex(
          (o) => o.id === paymentUpdate.orderId
        );
        if (orderIndex !== -1) {
          updatedOrders[orderIndex] = {
            ...updatedOrders[orderIndex],
            isPaid: paymentUpdate.paymentStatus === 'Completed',
          };
        }
      }
    );
  }

  return updatedOrders;
};

export const billerOrdersSlice = createSlice({
  name: 'billerOrders',
  initialState,
  reducers: {
    selectOrder: (state, action) => {
      state.selectedOrder = action.payload;
    },
    setSelectedOrderNeedsRefresh: (state, action) => {
      state.selectedOrderNeedsRefresh = action.payload;
    },
    updateOrders: (state, action) => {
      state.orders = processOrderNotification(state.orders, action.payload);

      // Always update selectedOrder to the latest reference or null
      if (state.selectedOrder) {
        const updatedOrder = state.orders.find(
          (o) => o.id === state.selectedOrder?.id
        );
        state.selectedOrder = updatedOrder || null;

        // If notification does not contain full updatedOrder for selectedOrder, trigger fetch
        const notification = action.payload;
        const fullOrderInNotification =
          notification.updatedOrders &&
          notification.updatedOrders.some(
            (o: Order) => o.id === state.selectedOrder?.id
          );
        if (!fullOrderInNotification && state.selectedOrder) {
          // Dispatch fetchBillerOrderDetailsAsync for selectedOrder (must be done in thunk, not reducer)
          // Set a flag for UI to trigger fetch if needed
          state.selectedOrderNeedsRefresh = true;
        } else {
          state.selectedOrderNeedsRefresh = false;
        }
      }
    },
    setConnectionStatus: (state, action) => {
      state.connectionStatus = action.payload; // Handle connection status
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBillerOrdersAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBillerOrdersAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.orders = action.payload;
      })
      .addCase(fetchBillerOrdersAsync.rejected, (state) => {
        state.isLoading = false;
        state.error = 'Failed to load orders';
      })
      .addCase(fetchBillerOrderDetailsAsync.fulfilled, (state, action) => {
        const updatedOrder = action.payload;
        if (
          !updatedOrder ||
          typeof updatedOrder !== 'object' ||
          updatedOrder.id === undefined
        ) {
          return;
        }
        // Always update or insert the order in the orders list with the latest details
        const idx = state.orders.findIndex((o) => o.id === updatedOrder.id);
        if (idx !== -1) {
          state.orders[idx] = { ...state.orders[idx], ...updatedOrder };
        } else {
          state.orders.unshift(updatedOrder);
        }
        if (state.selectedOrder?.id === updatedOrder.id) {
          state.selectedOrder = updatedOrder;
        }
      });
  },
});

export const {
  selectOrder,
  updateOrders,
  setConnectionStatus,
  setSelectedOrderNeedsRefresh,
} = billerOrdersSlice.actions;

export default billerOrdersSlice.reducer;
